"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, Building2, MapPin, ArrowRight, Clock, HardHat } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
const keyPoints = [{
  title: "Town centre & retail",
  desc: "Carefully planned public protection, pedestrian gantries, and out-of-hours erection for busy commercial zones.",
  icon: Building2
}, {
  title: "Residential developments",
  desc: "Full perimeter scaffolds, edge protection, and temporary roofs for high-density housing and extensions.",
  icon: HardHat
}, {
  title: "Office refurbishment",
  desc: "Internal birdcages, hoist towers, and multi-storey access tailored to live commercial refits across Surrey.",
  icon: Clock
}];
export default function WokingIntro() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 12
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4
      }
    }
  };
  return <section id="wokingscaffoldingintro" data-nav="light" className="relative bg-background py-16 md:py-24 text-foreground border-b border-border overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="max-w-4xl mx-auto">
          {/* Top badges */}
          <div className="flex flex-wrap items-center gap-2.5 mb-6">
            <Badge variant="outline" className="bg-accent/50 text-foreground border-border font-medium px-3 py-1 flex items-center gap-1.5">
              <MapPin className="size-3.5 text-primary" />
              <span>Woking &amp; Surrey</span>
            </Badge>
            <Badge variant="outline" className="bg-accent/50 text-foreground border-border font-medium px-3 py-1 flex items-center gap-1.5">
              <ShieldCheck className="size-3.5 text-primary" />
              <span>NASC &amp; CHAS accredited</span>
            </Badge>
          </div>

          {/* Main heading */}
          <motion.h1 variants={itemVariants} className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground mb-6">
            Scaffolding in Woking
          </motion.h1>

          {/* Answer-first localized narrative */}
          <motion.div variants={itemVariants} className="space-y-4 mb-10">
            <p className="text-lg md:text-xl text-foreground font-medium leading-relaxed">
              BMF Scaffolding provides commercial, residential, roofing, solar and specialist scaffolding in Woking and the surrounding Surrey area, including GU21 and GU22.
            </p>
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
              Whether you manage high-footfall town-centre commercial and retail
              premises, high-density residential developments, or multi-storey
              office refurbishments, we erect safe, compliant structures tailored
              to tight urban access and exacting programmes. Led by Pete Wood with
              over 20 years of hands-on industry experience, our qualified teams
              bring NASC and CHAS safety standards to every site, ensuring your
              contractors, trades, and the public remain fully protected.
            </p>
          </motion.div>

          {/* Three key localized project cards */}
          <motion.div variants={itemVariants} className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
            {keyPoints.map((point, i) => {
            const IconComp = point.icon;
            return <div data-index={i} key={point.title}>
                  <Card className="h-full bg-card border-border shadow-xs hover:border-primary/40 transition-[border-color,box-shadow]">
                    <CardContent className="pt-6 pb-6 px-5 flex flex-col justify-between h-full">
                      <div>
                        <div className="w-10 h-10 rounded-md bg-accent flex items-center justify-center mb-4 text-primary">
                          <IconComp className="size-5" />
                        </div>
                        <h2 className="text-base font-semibold text-card-foreground mb-2">
                          {point.title}
                        </h2>
                        <p className="text-sm text-muted-foreground leading-normal">
                          {point.desc}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </div>;
          })}
          </motion.div>

          {/* Action row */}
          <motion.div variants={itemVariants} className="flex flex-wrap items-center gap-4 pt-2 border-t border-border">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
              <Link to="/ContactRequestQuotePage">
                Request a quote
                <ArrowRight className="size-4 ml-1.5" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="border-border text-foreground hover:bg-accent">
              <Link to="/CommercialScaffoldingPage">
                View commercial scaffolding
              </Link>
            </Button>
            <span className="text-xs text-muted-foreground sm:ml-auto">
              20+ years leadership | Fast site surveys in GU21 &amp; GU22
            </span>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}